import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Camera, ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Terms of Service | 360Lumina"
        description="Read 360Lumina's Terms of Service. Learn about your rights and responsibilities when using our photography business management platform."
        canonical="https://www.360lumina.com/terms"
      />
      {/* Header */}
      <header className="border-b border-border py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Camera className="w-6 h-6 text-primary" />
            <span className="text-xl font-serif">360Lumina</span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-serif mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last Updated: January 1, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <p>
            These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") 
            and 360Lumina LLC ("360Lumina," "we," "us," or "our") governing your access to and use of the 360Lumina platform, 
            website, and related services (collectively, the "Platform"). By accessing or using the Platform, you acknowledge 
            that you have read, understood, and agree to be bound by these Terms. If you do not agree, you must not access or 
            use the Platform.
          </p>

          <section>
            <h2 className="text-2xl font-serif mb-4">1. Nature of the Platform</h2>
            <p>
              360Lumina is a software-as-a-service technology platform that enables independent photographers to manage bookings, 
              host digital galleries, communicate with clients, and facilitate payments through third-party payment processors. 
              360Lumina does not:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Provide photography services;</li>
              <li>Employ or manage photographers;</li>
              <li>Act as an agent, partner, or joint venturer of any photographer;</li>
              <li>Guarantee bookings, availability, quality, or outcomes of services.</li>
            </ul>
            <p className="mt-4">
              Any agreement for photography services is entered into solely between the photographer and the client.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">2. Eligibility</h2>
            <p>You represent and warrant that:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>You are at least eighteen (18) years of age;</li>
              <li>You have the legal capacity to enter into binding agreements;</li>
              <li>Your use of the Platform complies with all applicable laws and regulations.</li>
            </ul>
            <p className="mt-4">We reserve the right to deny or terminate access to any User at our sole discretion.</p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">3. Account Registration and Security</h2>
            <p>You are responsible for:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Maintaining the confidentiality of your login credentials;</li>
              <li>All activities occurring under your account;</li>
              <li>Providing accurate, current, and complete information.</li>
            </ul>
            <p className="mt-4">
              360Lumina shall not be liable for losses resulting from unauthorized account access due to your failure to safeguard credentials.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">4. Bookings and Services</h2>
            
            <h3 className="text-xl font-medium mt-6 mb-3">4.1 Clients</h3>
            <p>Clients acknowledge that:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Photography services are provided exclusively by photographers;</li>
              <li>360Lumina is not responsible for performance, delivery, or disputes;</li>
              <li>Any claims related to services must be resolved with the photographer.</li>
            </ul>

            <h3 className="text-xl font-medium mt-6 mb-3">4.2 Photographers</h3>
            <p>Photographers are solely responsible for:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Service delivery;</li>
              <li>Pricing, availability, cancellations, and refunds;</li>
              <li>Client communications;</li>
              <li>Compliance with applicable laws and tax obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">5. Payments and Fees</h2>
            <p>
              360Lumina utilizes third-party payment processors, including but not limited to Stripe, Inc. ("Payment Processors"). 
              360Lumina does not store or process credit card data; Platform fees may be deducted automatically; Payment timing, 
              availability, and disputes are governed by the Payment Processor's terms. 360Lumina disclaims all liability for 
              payment delays, chargebacks, processor errors, or banking issues.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">5A. Payment Processing and Stripe Connect</h2>
            <p>
              Payments on the Platform are processed by third-party payment processors, including Stripe, Inc. and its affiliates 
              ("Stripe"), through Stripe Connect. By using the Platform, you agree that:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>
                <strong>Stripe's Services Agreement Applies:</strong> Your use of payment processing services is subject to Stripe's terms, 
                including the Stripe Services Agreement and Stripe Connected Account Agreement (if applicable). These terms are available 
                at stripe.com/legal and may be updated by Stripe from time to time.
              </li>
              <li>
                <strong>Separate Relationship With Stripe:</strong> Stripe is an independent third party. 360Lumina is not responsible 
                for Stripe's services, actions, or omissions.
              </li>
              <li>
                <strong>Information Sharing:</strong> You authorize 360Lumina to share information about you and your transactions with 
                Stripe as necessary to provide payment services and comply with applicable laws, including identity verification, 
                fraud prevention, and regulatory requirements.
              </li>
              <li>
                <strong>Payouts and Funds Availability:</strong> Payout timing, availability, reserves, chargebacks, and payment reversals 
                are determined by Stripe and your financial institution, not by 360Lumina.
              </li>
              <li>
                <strong>Chargebacks and Disputes:</strong> You are solely responsible for chargebacks, disputes, refunds, penalties, 
                or fees assessed by Stripe related to your transactions.
              </li>
              <li>
                <strong>Suspension or Termination by Stripe:</strong> Stripe may suspend or terminate payment services at its discretion. 
                360Lumina shall not be liable for any resulting interruption of payment processing.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">6. Taxes</h2>
            <p>
              Photographers are solely responsible for determining, collecting, reporting, and remitting all applicable taxes. 
              360Lumina does not provide tax advice and assumes no tax liability on behalf of Users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">7. Intellectual Property and Content</h2>
            
            <h3 className="text-xl font-medium mt-6 mb-3">7.1 Photographer Content</h3>
            <p>
              Photographers retain all ownership rights in their content. By uploading content, photographers grant 360Lumina 
              a non-exclusive, royalty-free, worldwide license to host, store, display, transmit, and deliver such content 
              solely for Platform functionality.
            </p>

            <h3 className="text-xl font-medium mt-6 mb-3">7.2 Client Use</h3>
            <p>
              Clients may only use delivered content in accordance with the photographer's license or agreement. Unauthorized 
              reproduction or distribution is prohibited. You represent and warrant that you possess all rights necessary to 
              upload content to the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">8. Prohibited Conduct</h2>
            <p>Users shall not:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Upload unlawful, infringing, or harmful content;</li>
              <li>Violate intellectual property rights;</li>
              <li>Harass, defraud, or misrepresent;</li>
              <li>Attempt unauthorized access or security circumvention;</li>
              <li>Bypass or evade Platform fees.</li>
            </ul>
            <p className="mt-4">360Lumina may remove content or suspend accounts at its sole discretion.</p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">9. Copyright Infringement (DMCA)</h2>
            <p>
              360Lumina complies with the Digital Millennium Copyright Act ("DMCA"). Upon receipt of a valid notice, we may 
              remove allegedly infringing content and notify the uploader. Repeat infringement may result in account termination.
              For more information, see our <Link to="/dmca" className="text-primary hover:underline">DMCA Copyright Policy</Link>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">10. Availability and Disclaimer of Warranties</h2>
            <p className="uppercase font-medium">
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE." 360LUMINA DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, 
              INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT 
              GUARANTEE UNINTERRUPTED OR ERROR-FREE OPERATION.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">11. Limitation of Liability</h2>
            <p className="uppercase font-medium">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, 360LUMINA SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, 
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR DATA. OUR TOTAL LIABILITY SHALL NOT EXCEED 
              THE FEES PAID BY YOU TO 360LUMINA IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">12. Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless 360Lumina from any claims, damages, losses, or expenses arising from:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Your use of the Platform;</li>
              <li>Your content;</li>
              <li>Your services or conduct;</li>
              <li>Your violation of these Terms or applicable law.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">13. Termination</h2>
            <p>
              You may terminate your account at any time. 360Lumina may suspend or terminate access at its discretion, 
              including for violations of these Terms or legal requirements. Certain provisions shall survive termination.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">14. Arbitration and Class Action Waiver</h2>
            <p>
              Any dispute arising out of these Terms shall be resolved through binding arbitration, except where prohibited 
              by law. You waive any right to participate in a class action or representative proceeding.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">15. Modifications</h2>
            <p>
              360Lumina may modify these Terms at any time. Continued use of the Platform constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">16. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, 
              without regard to conflict-of-law principles.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">17. Minors and Consent</h2>
            <p>
              The Platform is not intended for use by individuals under the age of eighteen (18). Photographers represent 
              and warrant that:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>
                They have obtained all required parental or legal guardian consents, model releases, and permissions for 
                any content depicting minors;
              </li>
              <li>Such content is lawful to upload, host, and distribute;</li>
              <li>They have the authority to grant 360Lumina the license described in these Terms for such content.</li>
            </ul>
            <p className="mt-4">
              360Lumina does not verify the existence or validity of model releases and disclaims all responsibility for 
              obtaining or maintaining such documentation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">18. Contact Information</h2>
            <p>
              360Lumina LLC<br />
              Email: <a href="mailto:support@360lumina.com" className="text-primary hover:underline">support@360lumina.com</a>
            </p>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-12 pt-8 border-t border-border">
          <h3 className="text-lg font-medium mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
            <Link to="/dmca" className="text-primary hover:underline">DMCA Copyright Policy</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
